import React from 'react'
import * as SC from 'components/TailwindUIToolkit/StyledComponents'
import { assign, startCase } from 'lodash'
import {
  Home_Construction_Status_Enum_Enum,
  Home_Occupancy_Status_Enum_Enum,
  Home_Ownership_Status_Enum_Enum,
  Home_Summary_Status_Enum_Enum
} from 'graphql/generated'

import {
  BUYERS_INSPECTION_STATUSES,
  COUNTER_RESPONSE_TYPES
} from '@homevest/utils/buyers-inspections'
import {
  PROJECT_STATUSES,
  PROJECT_TRACKING_STATUSES,
  PROJECT_TYPES
} from '@homevest/utils/construction-projects'
import {
  DISPOSITION_COUNTERPARTY_TYPES,
  DISPOSITION_SALE_TYPES,
  DISPOSITION_STATUSES
} from '@homevest/utils/dispositions'
import { FUND_ABBREVIATIONS, FUND_NAMES } from '@homevest/utils/funds'
import {
  OCCUPANCY_INSPECTION_STATUSES,
  OCCUPANCY_INSPECTION_RESULTS
} from '@homevest/utils/occupancy-inspections'
import { REGIONS, STATES, STATES_FULL } from '@homevest/utils/regions'
import {
  LEASE_END_REASONS,
  RENTAL_PROGRAM_TYPES,
  RENTAL_PROGRAM_LEASE_TYPES
} from '@homevest/utils/rentals'
import { SAFERENT_REQUEST_STATUSES, SAFERENT_REQUEST_TYPES } from '@homevest/utils/saferent'
import { TICKET_CATEGORIES, TICKET_TYPES } from '@homevest/utils/tickets'
import {
  UTILITY_BILL_EXTERNAL_SOURCES,
  UTILITIES_RESPONSIBLE_PARTIES,
  UTILITY_TYPES
} from '@homevest/utils/utilities'
import { VERIFICATION_STATUSES } from '@homevest/utils/verifications'

import { UNDERWRITING_RECOMMENDATION_SUMMARIES } from 'lib/rental-application-underwriting'

type BadgeColor =
  | 'Yellow'
  | 'Green'
  | 'Red'
  | 'Gray'
  | 'Blue'
  | 'Orange'
  | 'Purple'
  | 'Teal'
  | 'Black'
  | 'Pink'

export type BadgeOptionOverrides = Partial<{
  colorOverride: BadgeColor
  dot: boolean
  fallbackText: string
  hoverText: string
  formatFn: (input: string) => string
  size: 'xs' | 'sm' | 'base' | 'lg' | 'xl'
  strikethrough: boolean
}>

// TOOD: these should be passed in by the caller so that we dont have collisions across statuses
export const mapStatusToBadgeColor: { [key: string]: BadgeColor } = {
  Yes: 'Green',
  No: 'Red',
  active: 'Green',
  // prequalification statuses
  needs_cosigner: 'Yellow',
  prequalified: 'Green',
  unqualified: 'Red',
  // funnel statuses
  applied: 'Blue',
  needs_standard_matching: 'Yellow',
  incomplete_tour: 'Yellow',
  needs_application_intro: 'Yellow',
  needs_tour_follow_up: 'Yellow',
  needs_priority_matching: 'Yellow',
  archived: 'Gray',
  // header statuses
  needs_prequalification: 'Yellow', // true if user does not have corresponding row in user_prequalification_data
  renter: 'Green', // true if user has active rental in rental_users table
  application_intro: 'Yellow', // might not be used
  canceled: 'Red',
  deleted: 'Red',
  // lead group user types (Gray if not listed)
  primary: 'Green',
  cosigner: 'Blue',
  // calls
  answeRed: 'Green',
  'no-answer': 'Red',
  'vm-answer': 'Yellow',
  'vm-left': 'Yellow',
  busy: 'Red',
  blocked: 'Red',
  error: 'Red',
  abandoned: 'Red',
  unknown: 'Gray',
  // rental app statuses
  created: 'Yellow',
  underwritten: 'Yellow',
  completed: 'Green',
  renewals: 'Orange',
  reviewed: 'Green',
  review_approved: 'Blue',
  review_unapproved: 'Red',
  complete: 'Green',
  pending_application_review: 'Yellow',
  pending_rental_agreement_creation: 'Yellow',
  pending_customer_information: 'Yellow',
  // documents
  pending_review: 'Yellow',
  valid: 'Green',
  invalid: 'Red',
  default: 'Green',
  pending: 'Yellow',
  Paid: 'Green',
  resolved: 'Green',
  //note categories
  access: 'Green',
  home_purchase: 'Green',
  move_in: 'Green',
  move_out: 'Yellow',
  work_order: 'Yellow',
  project_notes: 'Blue',
  insurance: 'Blue',
  collections: 'Red',
  evictions: 'Red',
  utilities: 'Yellow',
  // eviction statuses
  filed: 'Orange',
  consent_judgement: 'Purple',
  // occupancy
  [Home_Occupancy_Status_Enum_Enum.Standard]: 'Green',
  [Home_Occupancy_Status_Enum_Enum.Eviction]: 'Red',
  [Home_Occupancy_Status_Enum_Enum.VacantLeased]: 'Green',
  [Home_Occupancy_Status_Enum_Enum.Holdover]: 'Yellow',
  [Home_Occupancy_Status_Enum_Enum.NoticeLeased]: 'Green',
  [Home_Occupancy_Status_Enum_Enum.NoticeUnleased]: 'Yellow',
  [Home_Occupancy_Status_Enum_Enum.VacantUnleased]: 'Red',
  [Home_Occupancy_Status_Enum_Enum.Hold]: 'Yellow',
  // construction
  [Home_Construction_Status_Enum_Enum.None]: 'Gray',
  [Home_Construction_Status_Enum_Enum.AwaitingStart]: 'Gray',
  [Home_Construction_Status_Enum_Enum.InProgress]: 'Blue',
  // [Home_Construction_Status_Enum_Enum.PendingReview]: 'Yellow',
  [Home_Construction_Status_Enum_Enum.PendingWalkthrough]: 'Yellow',
  [Home_Construction_Status_Enum_Enum.RentReady]: 'Green',
  // [Home_Construction_Status_Enum_Enum.Canceled]: 'Red',
  [Home_Construction_Status_Enum_Enum.QualityCheck]: 'Blue',
  [Home_Construction_Status_Enum_Enum.TerminationRequested]: 'Yellow',
  // ownership
  [Home_Ownership_Status_Enum_Enum.NotOwned]: 'Gray',
  [Home_Ownership_Status_Enum_Enum.Bidding]: 'Blue',
  [Home_Ownership_Status_Enum_Enum.Screening]: 'Blue',
  [Home_Ownership_Status_Enum_Enum.Diligence]: 'Blue',
  [Home_Ownership_Status_Enum_Enum.Owned]: 'Green',
  [Home_Ownership_Status_Enum_Enum.Disposing]: 'Yellow',
  [Home_Ownership_Status_Enum_Enum.Sold]: 'Gray',
  [Home_Ownership_Status_Enum_Enum.Terminated]: 'Red',
  // summary
  // [Home_Summary_Status_Enum_Enum.NotOwned]: 'Gray',
  [Home_Summary_Status_Enum_Enum.Acquiring]: 'Blue',
  [Home_Summary_Status_Enum_Enum.AcquisitionTerminated]: 'Gray',
  [Home_Summary_Status_Enum_Enum.NotReadyLeased]: 'Yellow',
  [Home_Summary_Status_Enum_Enum.RentReadyLeased]: 'Green',
  [Home_Summary_Status_Enum_Enum.OccupiedEviction]: 'Yellow',
  [Home_Summary_Status_Enum_Enum.OccupiedHoldover]: 'Yellow',
  [Home_Summary_Status_Enum_Enum.OccupiedNotice]: 'Yellow',
  [Home_Summary_Status_Enum_Enum.OccupiedStandard]: 'Green',
  [Home_Summary_Status_Enum_Enum.RentReadyUnleased]: 'Yellow',
  [Home_Summary_Status_Enum_Enum.NotReadyUnleased]: 'Yellow',
  // [Home_Summary_Status_Enum_Enum.Sold]: 'Gray',
  // cant make it Red, duplicate unknown above
  // [Home_Summary_Status_Enum_Enum.Unknown]: 'Red',

  // Utility Responsibilities
  [UTILITIES_RESPONSIBLE_PARTIES.TENANT]: 'Green',
  [UTILITIES_RESPONSIBLE_PARTIES.UPANDUP]: 'Blue',

  // User Identity Verifications
  [VERIFICATION_STATUSES.VERIFIED]: 'Green',
  [VERIFICATION_STATUSES.PROCESSING]: 'Yellow',
  [VERIFICATION_STATUSES.SESSION_CREATED]: 'Yellow',
  [VERIFICATION_STATUSES.FAILED]: 'Red',

  // cashout statuses
  adjustments_finalized: 'Purple',
  utilities_finalized: 'Yellow',
  maintenance_chargebacks_finalized: 'Orange',
  paid_out: 'Green',
  // down statuses
  down: 'Red',
  approved: 'Green',
  rejected: 'Red',
  deferred: 'Yellow',

  // User Income virtual statuses
  submitted: 'Yellow',
  additional_income_submitted: 'Yellow',
  additional_income_requested: 'Orange',
  not_submitted: 'Orange',

  // User Income Source statuses
  edit_requested: 'Orange',

  // Underwriting recommendation summaries
  [UNDERWRITING_RECOMMENDATION_SUMMARIES.APPROVE]: 'Green',
  [UNDERWRITING_RECOMMENDATION_SUMMARIES.APPROVE_WITH_ADDITIONAL_DEPOSIT]: 'Orange',
  [UNDERWRITING_RECOMMENDATION_SUMMARIES.REJECT]: 'Red',
  [UNDERWRITING_RECOMMENDATION_SUMMARIES.MANUAL_REVIEW]: 'Yellow',
  [UNDERWRITING_RECOMMENDATION_SUMMARIES.PREAPPROVE]: 'Teal',

  // Vanilla vs. Up&Up lease config
  // [RENTAL_PROGRAM_TYPES.UPANDUP]: 'Blue',
  [RENTAL_PROGRAM_TYPES.VANILLA]: 'Yellow',
  [RENTAL_PROGRAM_LEASE_TYPES.upandup]: 'Blue',
  [RENTAL_PROGRAM_LEASE_TYPES.vanilla]: 'Yellow',

  // Ticket types
  [TICKET_TYPES.REHAB]: 'Red',
  [TICKET_TYPES.DISPOSITION]: 'Yellow',
  [TICKET_TYPES.TURN]: 'Blue',
  [TICKET_TYPES.MAINTENANCE]: 'Orange',

  // Construction project types
  // [PROJECT_TYPES.TURN]: 'Green',
  [PROJECT_TYPES.TURN_OI]: 'Blue',
  // [PROJECT_TYPES.DISPOSITION]: 'Purple',
  [PROJECT_TYPES.DISPOSITION_INSPECTION]: 'Yellow',
  [PROJECT_TYPES.DISPOSITION_OI]: 'Orange',
  // [PROJECT_TYPES.REHAB]: 'Blue',

  // Construction project statuses
  [PROJECT_STATUSES.PENDING_MOVE_OUT]: 'Gray',
  [PROJECT_STATUSES.PENDING_MOVE_OUT_INSPECTION]: 'Yellow',
  [PROJECT_STATUSES.PENDING_SCOPE]: 'Yellow',
  [PROJECT_STATUSES.PENDING_SCOPE_APPROVAL]: 'Yellow',
  [PROJECT_STATUSES.PENDING_DISPATCH]: 'Yellow',
  [PROJECT_STATUSES.PENDING_CONSTRUCTION_START]: 'Blue',
  [PROJECT_STATUSES.PENDING_CONSTRUCTION_COMPLETION]: 'Blue',
  [PROJECT_STATUSES.PENDING_QUALITY_CONTROL]: 'Orange',
  // [PROJECT_STATUSES.POST_QUALITY_CONTROL_TASKS]: 'Orange',
  // [PROJECT_STATUSES.COMPLETE]: 'Green',
  // [PROJECT_STATUSES.CANCELED]: 'Red',

  // Construction project tracking statuses
  [PROJECT_TRACKING_STATUSES.ON_TRACK]: 'Green',
  [PROJECT_TRACKING_STATUSES.ESCALATED]: 'Purple',

  // Construction project substatuses
  // [PROJECT_SUBSTATUSES.NOT_STARTED]: 'Gray',
  // [PROJECT_SUBSTATUSES.IN_PROGRESS]: 'Blue',
  // [PROJECT_SUBSTATUSES.BLOCKED]: 'Red',
  // [PROJECT_SUBSTATUSES.COMPLETE]: 'Green',

  // Ticket categories
  [TICKET_CATEGORIES.REKEY]: 'Orange',
  [TICKET_CATEGORIES.PAINTING]: 'Yellow',
  [TICKET_CATEGORIES.SECURITY_INSTALL]: 'Purple',
  [TICKET_CATEGORIES.SECURITY_SYSTEM_REMOVAL]: 'Yellow',
  [TICKET_CATEGORIES.MOVE_OUT_INSPECTION]: 'Orange',
  [TICKET_CATEGORIES.QC_INSPECTION]: 'Red',
  [TICKET_CATEGORIES.SNOW_REMOVAL]: 'Blue',
  [TICKET_CATEGORIES.LANDSCAPING]: 'Yellow',
  [TICKET_CATEGORIES.TREE_REMOVAL]: 'Green',
  [TICKET_CATEGORIES.ROOF]: 'Red',
  [TICKET_CATEGORIES.CLEANING]: 'Blue',
  [TICKET_CATEGORIES.HANDYMAN]: 'Orange',
  [TICKET_CATEGORIES.PLUMBING]: 'Blue',
  [TICKET_CATEGORIES.ELECTRICAL]: 'Yellow',
  [TICKET_CATEGORIES.TRASH_REMOVAL]: 'Purple',
  [TICKET_CATEGORIES.APPLIANCES]: 'Purple',
  [TICKET_CATEGORIES.COUNTERTOPS]: 'Red',
  [TICKET_CATEGORIES.HVAC]: 'Gray',
  [TICKET_CATEGORIES.PEST_CONTROL]: 'Orange',
  [TICKET_CATEGORIES.OCCUPANCY_INSPECTION]: 'Gray',
  [TICKET_CATEGORIES.MISC]: 'Gray',

  // Buyers Inspection statuses
  [BUYERS_INSPECTION_STATUSES.SUBMITTED_BY_BUYER]: 'Yellow',
  [BUYERS_INSPECTION_STATUSES.VENDOR_BIDS_SENT]: 'Blue',
  [BUYERS_INSPECTION_STATUSES.COUNTER_SUBMITTED]: 'Green',
  [BUYERS_INSPECTION_STATUSES.COUNTER_RESPONSE_RECEIVED]: 'Green',
  // [BUYERS_INSPECTION_STATUSES.CANCELED]: 'Red'

  // Buyers Inspection Counter Response types
  [COUNTER_RESPONSE_TYPES.ACCEPTED]: 'Green',
  [COUNTER_RESPONSE_TYPES.COUNTERED]: 'Yellow',
  // [COUNTER_RESPONSE_TYPES.REJECTED]: 'Red'

  // Disposition statuses
  [DISPOSITION_STATUSES.ACTIVE_ON_MLS]: 'Blue',
  [DISPOSITION_STATUSES.CLEAR_TO_CLOSE]: 'Green',
  [DISPOSITION_STATUSES.LISTING_PREP]: 'Orange',
  [DISPOSITION_STATUSES.PENDING_DUE_DILIGENCE]: 'Purple',
  // [DISPOSITION_STATUSES.PENDING_MOVE_OUT]: 'Yellow',
  [DISPOSITION_STATUSES.PRE_CONTRACT_AGREEMENT]: 'Yellow',
  // [DISPOSITION_STATUSES.CANCELED]: 'Red',
  // [DISPOSITION_STATUSES.SOLD]: 'Green'

  // Disposition counterparty types
  [DISPOSITION_COUNTERPARTY_TYPES.CUSTOMER]: 'Green',
  [DISPOSITION_COUNTERPARTY_TYPES.INVESTOR]: 'Purple',
  [DISPOSITION_COUNTERPARTY_TYPES.OWNER_OCCUPANT]: 'Blue',

  // Disposition agent feedback
  not_interested: 'Red',
  maybe: 'Yellow',
  somewhat: 'Orange',
  yes: 'Blue',
  very: 'Green',
  poor: 'Red',
  fair: 'Yellow',
  good: 'Blue',
  excellent: 'Green',
  too_high: 'Red',
  just_right: 'Green',
  too_low: 'Orange',

  // Occupancy Inspection statuses
  [OCCUPANCY_INSPECTION_STATUSES.PENDING_BASE_PROJECT_COMPLETION]: 'Gray',
  [OCCUPANCY_INSPECTION_STATUSES.PENDING_APPLICATION]: 'Red',
  [OCCUPANCY_INSPECTION_STATUSES.APPLICATION_IN_PROGRESS]: 'Red',
  [OCCUPANCY_INSPECTION_STATUSES.PENDING_SCHEDULING]: 'Orange',
  [OCCUPANCY_INSPECTION_STATUSES.SCHEDULED]: 'Green',
  // [OCCUPANCY_INSPECTION_STATUSES.PENDING_SCOPE]: 'Yellow',
  [OCCUPANCY_INSPECTION_STATUSES.PENDING_APPROVAL]: 'Yellow',
  // [OCCUPANCY_INSPECTION_STATUSES.PENDING_CONSTRUCTION_START]: 'Blue',
  // [OCCUPANCY_INSPECTION_STATUSES.PENDING_CONSTRUCTION_COMPLETION]: 'Blue',
  // [OCCUPANCY_INSPECTION_STATUSES.PENDING_QUALITY_CONTROL]: 'Orange',
  [OCCUPANCY_INSPECTION_STATUSES.PENDING_OCCUPANT_SIGNATURE]: 'Purple',
  // [OCCUPANCY_INSPECTION_STATUSES.COMPLETE]: 'Green',

  // Occupancy Inspection Results
  [OCCUPANCY_INSPECTION_RESULTS.HARD_FAIL]: 'Red',
  [OCCUPANCY_INSPECTION_RESULTS.PASS]: 'Green',
  [OCCUPANCY_INSPECTION_RESULTS.SOFT_FAIL]: 'Yellow',

  // Utility types
  [UTILITY_TYPES.ELECTRIC]: 'Yellow',
  [UTILITY_TYPES.GAS]: 'Orange',
  [UTILITY_TYPES.WATER]: 'Blue',
  [UTILITY_TYPES.SEWER]: 'Green',
  [UTILITY_TYPES.STORMWATER]: 'Teal',
  [UTILITY_TYPES.TRASH]: 'Gray',

  // Utility bill external sources
  [UTILITY_BILL_EXTERNAL_SOURCES.CONSERVICE]: 'Gray',
  [UTILITY_BILL_EXTERNAL_SOURCES.NUTILITI]: 'Purple',

  // Funds
  [FUND_ABBREVIATIONS.FUND_1_CA]: 'Gray',
  [FUND_NAMES.FUND_1_CA]: 'Gray',
  [FUND_ABBREVIATIONS.FUND_1_MO]: 'Green',
  [FUND_NAMES.FUND_1_MO]: 'Green',
  [FUND_ABBREVIATIONS.FUND_2]: 'Blue',
  [FUND_NAMES.FUND_2]: 'Blue',
  [FUND_ABBREVIATIONS.WAREHOUSE]: 'Gray',
  [FUND_NAMES.WAREHOUSE]: 'Gray',
  [FUND_ABBREVIATIONS.GP_SEED_FUND_1]: 'Purple',
  [FUND_NAMES.GP_SEED_FUND_1]: 'Purple',
  [FUND_ABBREVIATIONS.HREP_III]: 'Red',
  [FUND_NAMES.HREP_III]: 'Red',
  [FUND_ABBREVIATIONS.HREP_IV]: 'Yellow',
  [FUND_NAMES.HREP_IV]: 'Yellow',
  [FUND_ABBREVIATIONS.GP_SEED_FUND_II]: 'Orange',
  [FUND_NAMES.GP_SEED_FUND_II]: 'Orange',
  [FUND_ABBREVIATIONS.F4_KC]: 'Teal',
  [FUND_NAMES.F4_KC]: 'Teal',

  // Markets
  [REGIONS.ATLANTA]: 'Orange',
  Atlanta: 'Orange',
  [REGIONS.CHARLOTTE]: 'Blue',
  Charlotte: 'Blue',
  [REGIONS.DALLAS]: 'Purple',
  Dallas: 'Purple',
  [REGIONS.HUNTSVILLE]: 'Red',
  Huntsville: 'Red',
  [REGIONS.INDIANAPOLIS]: 'Yellow',
  Indianapolis: 'Yellow',
  [REGIONS.KANSAS_CITY]: 'Teal',
  'Kansas City': 'Teal',
  [REGIONS.LOS_ANGELES]: 'Gray',
  'Los Angeles': 'Gray',
  [REGIONS.PHOENIX]: 'Pink',
  Phoenix: 'Pink',
  [REGIONS.RALEIGH]: 'Green',
  Raleigh: 'Green',
  [REGIONS.ST_LOUIS]: 'Green',
  'St. Louis': 'Green',
  [REGIONS.TAMPA]: 'Black',
  Tampa: 'Black',

  // States
  [STATES.AL]: 'Red',
  [STATES_FULL.AL]: 'Red',
  [STATES.CA]: 'Gray',
  [STATES_FULL.CA]: 'Gray',
  [STATES.GA]: 'Orange',
  [STATES_FULL.GA]: 'Orange',
  [STATES.IN]: 'Yellow',
  [STATES_FULL.IN]: 'Yellow',
  [STATES.MO]: 'Green',
  [STATES_FULL.MO]: 'Green',
  [STATES.NC]: 'Blue',
  [STATES_FULL.NC]: 'Blue',

  // Lease end reasons
  // cancelled
  [LEASE_END_REASONS.EVICTED]: 'Red',
  [LEASE_END_REASONS.VACATED_IN_EVICTIONS]: 'Red',
  [LEASE_END_REASONS.ABANDONED_LEASE]: 'Red',
  [LEASE_END_REASONS.EARLY_TERMINATION_CASUALTY_LOSS]: 'Orange',
  [LEASE_END_REASONS.EARLY_TERMINATION_MILITARY]: 'Orange',
  [LEASE_END_REASONS.EARLY_TERMINATION_MAINTAINANCE_ISSUES]: 'Orange',
  [LEASE_END_REASONS.EARLY_TERMINATION_CUSTOMER_SERVICE_ISSUES]: 'Orange',
  [LEASE_END_REASONS.EARLY_TERMINATION_OTHER]: 'Orange',
  [LEASE_END_REASONS.NEVER_MOVED_IN]: 'Gray',
  [LEASE_END_REASONS.PURCHASED_HOME_DISPO]: 'Blue',
  // completed
  [LEASE_END_REASONS.TRANSFERRED_HOMES]: 'Green',
  [LEASE_END_REASONS.PURCHASED_OTHER_HOME]: 'Green',
  [LEASE_END_REASONS.PRICE_TOO_HIGH]: 'Green',
  [LEASE_END_REASONS.CHANGE_OF_ROOMMATES]: 'Green',
  [LEASE_END_REASONS.MOVING_OUT_OF_AREA]: 'Green',
  [LEASE_END_REASONS.MAINTAINANCE_ISSUES]: 'Orange',
  [LEASE_END_REASONS.CUSTOMER_SERVICE_ISSUES]: 'Orange',
  [LEASE_END_REASONS.NEED_LARGER_HOME]: 'Green',
  [LEASE_END_REASONS.NEED_SMALLER_HOME]: 'Green',
  [LEASE_END_REASONS.DONT_LIKE_NEIGHBORHOOD]: 'Green',
  [LEASE_END_REASONS.PROBLEMS_WITH_NEIGHBORS]: 'Green',
  [LEASE_END_REASONS.DO_NOT_RENEW]: 'Yellow',

  // Saferent request statuses
  [SAFERENT_REQUEST_STATUSES.INTERNAL_ERROR]: 'Red',
  [SAFERENT_REQUEST_STATUSES.SAFERENT_ERROR]: 'Red',
  [SAFERENT_REQUEST_STATUSES.ARGYLE]: 'Blue',
  [SAFERENT_REQUEST_STATUSES.READY]: 'Green',
  [SAFERENT_REQUEST_STATUSES.RESUBMIT]: 'Yellow',
  [SAFERENT_REQUEST_STATUSES.PARTIAL]: 'Orange',
  [SAFERENT_REQUEST_STATUSES.PENDING]: 'Yellow',

  // Saferent request types
  [SAFERENT_REQUEST_TYPES.INITIAL]: 'Purple',
  [SAFERENT_REQUEST_TYPES.FINAL]: 'Pink',

  // disposition sale types
  [DISPOSITION_SALE_TYPES.SALE_TO_MARKET]: 'Blue',
  [DISPOSITION_SALE_TYPES.CUSTOMER_PURCHASE]: 'Green',
  [DISPOSITION_SALE_TYPES.CUSTOMER_PURCHASE_OR_SALE_TO_MARKET]: 'Purple'
}

export const mapContentToBadge = (
  status: string,
  optionOverrides: BadgeOptionOverrides = { dot: true, size: 'sm' }
) => {
  const options = assign({ dot: true, size: 'sm' }, optionOverrides)
  const badgeColor = options.colorOverride || mapStatusToBadgeColor[status] || 'Gray'
  const { dot, fallbackText, formatFn, hoverText, strikethrough, size } = options

  if (!status) {
    status = fallbackText || 'none'
  }
  if (formatFn) {
    status = formatFn(status)
  } else {
    status = startCase(status).replace('Upandup', 'Up&Up')
  }
  const badgeName = `${badgeColor}Badge` as const
  const dotName = `${badgeColor}Dot` as const
  const BadgeComponent = SC[badgeName]
  const DotComponent = SC[dotName]

  return (
    <BadgeComponent title={hoverText}>
      {dot ? <DotComponent /> : ' '}
      <SC.BadgeContent
        className={strikethrough ? 'line-through' : '' + size ? 'text-' + size : 'text-sm'}
        dot={dot}
      >
        {status}
      </SC.BadgeContent>
    </BadgeComponent>
  )
}
